import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { NumberBadge } from '@/components/ui/DataTable/NumberBadge'
import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { RestSchemas } from '@/contexts/QueryProvider'
import { getInitials } from '@/lib/utils'
import { IconAvatarOutline } from '@icons/IconAvatarOutline'
import { IconCommunityOutline } from '@icons/IconCommunityOutline'
import { IconOpenInNewOutline } from '@icons/IconOpenInNewOutline'
import { IconWebcastOutline } from '@icons/IconWebcastOutline'
import { ColumnDef } from '@tanstack/react-table'
import clsx from 'clsx'
import { format } from 'date-fns'
import { Link } from 'react-router'
import { OrganisationWorkspaceRowActions } from './OrganisationWorkspaceRowActions'

type MessagesSubscriptionsOrganisationWorkspaceSummary =
  RestSchemas['Messages.Subscriptions.OrganisationWorkspaceSummary']

export const columns: ColumnDef<MessagesSubscriptionsOrganisationWorkspaceSummary>[] = [
  {
    id: 'workspace',
    header: ({ column }) => <SortingHeaderButton column={column}>Workspace</SortingHeaderButton>,
    accessorFn: (row) => clsx(row.projectName, row.applicationName),
    cell: ({ row }) => {
      const item = row.original
      return (
        <Link className="text-foreground" to={`/workspaces/${item.workspaceId.toString()}`}>
          <TooltipButton tooltip={`${item.applicationName} | ${item.projectName}`}>
            <div className="group flex items-center gap-x-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <IconWebcastOutline className="size-5 text-primary" />
              </div>

              <span className="truncate text-sm text-primary group-hover:underline">
                {item.applicationName} | {item.projectName}
              </span>
              <IconOpenInNewOutline className="ml-auto mr-0 hidden size-4 shrink-0 self-center text-primary group-hover:block" />
            </div>
          </TooltipButton>
        </Link>
      )
    },
    size: 300,
    sortingFn: (rowA, rowB) => rowA.original.applicationName.localeCompare(rowB.original.applicationName),
  },
  {
    id: 'supportTeams',
    header: 'Support Teams',
    accessorFn: (row) => row.supportTeams,
    cell: ({ row }) => {
      const item = row.original
      const count = item.supportTeams
      const summary = count > 0 && item.supportTeamsSummary ? ` ${item.supportTeamsSummary}` : ''
      return (
        <div className="group flex items-center gap-x-4">
          <IconCommunityOutline className={clsx('size-5 text-muted-foreground')} />
          <NumberBadge value={count} />
          <span className="text-xs italic text-muted-foreground">{summary}</span>
        </div>
      )
    },
  },
  {
    id: 'projectTeams',
    header: 'Project Teams',
    accessorFn: (row) => row.projectTeams,
    cell: ({ row }) => {
      const item = row.original
      const count = item.projectTeams
      const summary = count > 0 && item.projectTeamsSummary ? ` ${item.projectTeamsSummary}` : ''
      return (
        <div className="group flex items-center gap-x-4">
          <IconCommunityOutline className={clsx('size-5 text-muted-foreground')} />
          <NumberBadge value={count} />
          <span className="text-xs italic text-muted-foreground">{summary}</span>
        </div>
      )
    },
  },
  {
    id: 'directLinks',
    header: 'Direct linked users',
    accessorFn: (row) => row.directLinks,
    cell: ({ row }) => {
      const item = row.original
      return (
        <div className="group flex items-center gap-x-4">
          <IconAvatarOutline className={clsx('size-5 text-muted-foreground')} />
          <NumberBadge value={item.directLinks} />
        </div>
      )
    },
  },
  {
    id: 'approver',
    header: ({ column }) => <SortingHeaderButton column={column}>Approver</SortingHeaderButton>,
    accessorFn: (row) => row.audits.approver,
    cell: ({ row }) => {
      const item = row.original
      const [name, email] = (item.audits.approver || '').split('\n')
      if (!name.trim() && !email.trim()) return null
      return (
        <div className="group flex items-center gap-x-4">
          <TooltipButton tooltip={`${name} : ${email}`}>
            <Avatar className="ring-2 ring-background">
              <AvatarFallback className="bg-accent text-accent-foreground">{getInitials(name)}</AvatarFallback>
            </Avatar>
          </TooltipButton>
        </div>
      )
    },
  },
  {
    id: 'approvalDate',
    header: ({ column }) => <SortingHeaderButton column={column}>Approval Date</SortingHeaderButton>,
    accessorFn: (row) => row.audits.approvalDate,
    cell: ({ row }) => {
      const item = row.original
      if (!item.audits.approvalDate) return null
      return <div>{format(item.audits.approvalDate, 'PP')}</div>
    },
  },
  {
    id: 'review',
    cell: ({ row }) => {
      const item = row.original
      const hasUsers =
        item.directLinks > 0 || item.supportTeamsMembers > 0 || item.projectTeamsMembers > 0
      return (
        <OrganisationWorkspaceRowActions workspaceId={item.workspaceId} hasUsers={hasUsers} />
      )
    },
  },
]
